import React, { useEffect, useState } from 'react'
import axios from 'axios';
import { Form } from '@formio/react';
import { ONBOARDAPIURL } from '../../../config';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import SignatureModal from "../../../components/Billing/SignatureCap/SignatureModal";
import moment from 'moment';

const IntakeForm = (props) => {
    const [submitted, setSubmitted] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedForm, setSelectedForm] = useState(null);
    const [data, setData] = useState(null);
    const [errors, setErrors] = useState([]);
    const token = props.match.params.token.split('?')[0];
    const [signatureModal, setSignatureModal] = useState(false);
    const [signCaptured, setSignCaptured] = useState(false);
    const [submissionError, setSubmissionError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const getSubmissionError = (error) => {
        const message = error?.response?.data?.error;
        if (message === 'Token not found') {
            return 'This form link could not be found. Reopen the link from your email or ask the sender for a new link.';
        }
        return typeof message === 'string' ? message : 'Your forms could not be saved. Please try again. Your answers are still open.';
    };

    useEffect(() => {
        let cancelled = false;
        if (!token) {
            setLoadError('This form link is missing its access token. Ask the sender for a new link.');
            setLoading(false);
            return () => { cancelled = true; };
        }
        const getData = async () => {
            try {
                const response = await axios.get(ONBOARDAPIURL + '/intake', { params: { token } });
                const forms = (response.data.forms || []).map((form) => ({ ...form, completed: Boolean(form.data) }));
                const tokenObj = response.data.token;
                const entityData = JSON.parse(tokenObj.entity_data);
                const companyLogo = response.data.companyLogo;
                if (!cancelled) {
                    setData({
                        forms,
                        task_id: entityData.task_id,
                        customer_id: entityData.customer_id,
                        customer_name: entityData.customer_name,
                        employee_id: tokenObj.created_by,
                        signature_required: entityData.signature_required,
                        expires_at: tokenObj.expires_at,
                        company_name: entityData.company_name,
                        company_logo: companyLogo
                            ? `data:${companyLogo.type};base64,${arrayBufferToBase64(companyLogo.logo.data)}`
                            : null,
                    });
                }
            } catch (error) {
                if (!cancelled) setLoadError(error?.response?.data?.error || 'This form link is unavailable. Contact the sender for a new link.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        getData();
        return () => { cancelled = true; };
    }, [token]);

    const arrayBufferToBase64 = (buffer) => {
        let binary = '';
        let bytes = [].slice.call(new Uint8Array(buffer));

        bytes.forEach((b) => binary += String.fromCharCode(b));

        return window.btoa(binary);
    }

    const handleSubmit = async (submission) => {
        setSubmissionError('');
        try { 
            if (data.signature_required && !signCaptured) {
                setSignatureModal(true);
                return;
            }         
            setSubmitting(true);
            // App-wide Axios defaults use URL-encoded headers; this API expects JSON.
            await axios.post(ONBOARDAPIURL + '/intake/submit', { token: token }, {
                headers: { 'Content-Type': 'application/json' },
            });
            setSubmitted(true);
        } catch (error) {
            setSubmissionError(getSubmissionError(error));
        } finally {
            setSubmitting(false);
        }
    }

    const saveForm = async (submission) => {
        setSubmissionError('');
        setSubmitting(true);
        try {
            const formId = selectedForm.form_id;
            await axios.post(ONBOARDAPIURL + '/intake', { token: token, form: { form_id: formId, data: submission } }, {
                headers: { 'Content-Type': 'application/json' },
            });

            // Do not mutate completion state until the server confirms the save.
            setData((previous) => ({
                ...previous,
                forms: previous.forms.map((form) => form.form_id === formId
                    ? { ...form, completed: true, data: submission }
                    : form),
            }));
            setOpen(false);
        } catch (error) {
            setSubmissionError(getSubmissionError(error));
        } finally {
            setSubmitting(false);
        }
    }

    const getFormSchema = (form) => {
        try {
            return JSON.parse(form?.translated_json_schema || form?.json_schema || '{}');
        } catch (_) {
            return {};
        }
    };

    if (loading) {
        return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#f5f5f5' }}><CircularProgress sx={{ color: '#3FA7D6' }} /></Box>;
    }

    if (loadError || !data) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', p: 2 }}>
                <Box sx={{ maxWidth: 680, mx: 'auto', pt: { xs: 5, sm: 10 } }}>
                    <Alert severity="error">{loadError || 'This form link is unavailable. Contact the sender for a new link.'}</Alert>
                </Box>
            </Box>
        );
    }

    const completedCount = data.forms.filter((form) => form.completed).length;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', color: '#333', pb: 8, '& .MuiButton-contained': { bgcolor: '#3FA7D6', '&:hover': { bgcolor: '#3595bf' } }, '& .MuiButton-outlined': { color: '#3FA7D6', borderColor: '#3FA7D6' } }}>
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #ddd', px: 3, py: 2 }}>
                <Box sx={{ maxWidth: 1000, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    {data.company_logo && <Box component="img" src={data.company_logo} alt={`${data.company_name} logo`} sx={{ width: 52, height: 52, objectFit: 'contain', bgcolor: 'white', borderRadius: 1, p: 0.5 }} />}
                    <Box>
                        <Typography variant="overline" sx={{ color: '#3FA7D6' }}>INTAKE FORMS</Typography>
                        <Typography variant="h6">{data.company_name}</Typography>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ maxWidth: 820, mx: 'auto', px: 2, pt: { xs: 4, sm: 7 } }}>
                <Typography variant="overline" sx={{ color: '#666', fontWeight: 700 }}>FOR {data.customer_name}</Typography>
                <Typography variant="h3" sx={{ fontSize: { xs: 32, sm: 42 }, fontWeight: 700, mt: 1 }}>Complete your intake</Typography>
                <Typography sx={{ mt: 2, color: '#666', maxWidth: 650 }}>
                    Complete each form below. Your progress is saved as you go, and you can return to this link before submitting everything.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3, mb: 4, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <Chip label={`${completedCount} of ${data.forms.length} forms complete`} sx={{ bgcolor: 'rgb(235, 248, 255)', color: '#333', '& .MuiChip-label': { px: 1.5 } }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>Due {moment(data.expires_at).format('MMMM Do, YYYY')}</Typography>
                </Stack>
                {submissionError && !open && <Alert severity="error" sx={{ mb: 2 }}>{submissionError}</Alert>}
                {submitted ? (
                    <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, border: '1px solid #ddd', textAlign: 'center' }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#3FA7D6' }} />
                        <Typography variant="h4" sx={{ mt: 2 }}>Intake submitted</Typography>
                        <Typography sx={{ mt: 1, color: '#666' }}>Thank you. Your forms have been received.</Typography>
                    </Paper>
                ) : (
                    <>
                        <Stack spacing={1.5}>
                            {data.forms.map((form, index) => (
                                <Paper key={form.form_id} elevation={0} sx={{ border: '1px solid #ddd', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography sx={{ color: '#888', fontWeight: 700, minWidth: 28 }}>{String(index + 1).padStart(2, '0')}</Typography>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" sx={{ fontSize: 17 }}>{form.form_name}</Typography>
                                        {form.form_description && <Typography variant="body2" sx={{ color: '#666' }}>{form.form_description}</Typography>}
                                    </Box>
                                    {form.completed && <CheckCircleOutlinedIcon sx={{ color: '#3FA7D6' }} />}
                                    <Button variant={form.completed ? 'outlined' : 'contained'} onClick={() => { setSelectedForm(form); setOpen(true); }}>
                                        {form.completed ? 'Review' : 'Complete'}
                                    </Button>
                                </Paper>
                            ))}
                        </Stack>
                        {data.forms.length === 0 && <Alert severity="warning" sx={{ mt: 2 }}>There are no forms attached to this intake link. Contact the sender.</Alert>}
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            size="large"
                            sx={{ mt: 4, minWidth: 220 }}
                            disabled={submitting || data.forms.length === 0 || data.forms.some((form) => !form.completed)}
                        >
                            {submitting ? 'Submitting...' : 'Submit all forms'}
                        </Button>
                    </>
                )}
            </Box>
            <Dialog open={open} onClose={() => !submitting && setOpen(false)} maxWidth="md" fullWidth scroll="body" disableRestoreFocus>
                <DialogTitle sx={{ borderBottom: '1px solid #ddd' }}>{selectedForm?.form_name}</DialogTitle>
                <DialogContent>
                    {submissionError && <Alert severity="error" sx={{ mb: 2 }}>{submissionError}</Alert>}
                    {selectedForm && <Form form={getFormSchema(selectedForm)} submission={selectedForm.data} onSubmit={saveForm} onError={setErrors} />}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #ddd', px: 3, py: 2 }}>
                    <Button variant="outlined" onClick={() => setOpen(false)} disabled={submitting}>Close</Button>
                </DialogActions>
            </Dialog>
            <SignatureModal
                task={{ cusInfo: [{ cus_id: data.customer_id, cus_name: data.customer_name }], tas_pro_id: null, tas_id: data.task_id }}
                emp_id={data.employee_id}
                show={signatureModal}
                toggle={() => setSignatureModal((previous) => !previous)}
                captured={() => setSignCaptured(true)}
            />
        </Box>
    );
}

export default IntakeForm;